// MACHINERY V2 (Stroje) — THE v2 ERROR-WITH-RETRY PRIMITIVE (M2.5).
//
// >>> NEW SHARED V2 PRIMITIVE. IMPORT IT — DO NOT REINVENT. <<<
//
// WHY IT EXISTS: three of the four M2.5 tabs had to grow the SAME missing
// surface (ticket §7 fix 5, artboards 1002 + 1010, both labelled "PRIDANÝ
// STAV" / ADDED). Before this, a rejected fetch produced nothing a user could
// see or act on:
//   · SR list  — customerMachineServiceReportsSlice.ts:130-133 sets `error` and
//                clears `loading`; nothing in the v1 tab reads `error`.
//   · Specs    — machineCatalogSlice.ts:136-138 sets ONLY
//                `selectedModelLoading = false`. There is no error field at all.
//   · Parts    — customerMachineSlice.ts has NO extraReducer case for
//                `unlinkStockItems` (verified: the builder chain at :143-152
//                ends with installStockItems.rejected); a failed unlink writes
//                nothing anywhere.
// (Line numbers verified in this worktree at c15fb351 — re-verify before quoting
// them again, per the ticket's citation-rot rule.)
//
// NOT A TOAST. ToastV2 is the right surface for the outcome of an action the
// user just triggered and can forget about. This is the opposite case: the
// content the user came for is MISSING, the panel must stay on screen until the
// data arrives, and it has to carry the retry affordance itself. Both surfaces
// exist for that reason; do not replace one with the other.
//
// NOT `Notice` FROM fleet/FleetNotices.tsx either — that is the neutral centred
// empty/loading panel and has no action slot. This one is amber, has a title +
// hint + retry button, and lives in feedback/ next to ToastV2 because it IS a
// feedback surface, not a fleet one.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";

export interface RetryNoticeProps {
  /** What failed, in the user's words. Already translated. */
  title: string;
  /** What to do about it. Already translated. Optional. */
  hint?: string;
  /** Omit to render an explanation with no action (a failure the user cannot
   *  retry). A rendered button is a PROMISE that pressing it re-runs the thing
   *  that failed. */
  onRetry?: () => void;
  /** True while the retry is in flight — the button dims and stops accepting
   *  clicks so a slow endpoint cannot be hammered. */
  busy?: boolean;
}

const WarnGlyph: React.FC = () => (
  <svg viewBox="0 0 14 14" width="15" height="15" aria-hidden>
    <path
      d="M7 1.6 L13 12 L1 12 Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <rect x="6.45" y="5" width="1.1" height="3.4" rx="0.55" fill="currentColor" />
    <rect x="6.45" y="9.3" width="1.1" height="1.1" rx="0.55" fill="currentColor" />
  </svg>
);

const RetryNotice: React.FC<RetryNoticeProps> = ({ title, hint, onRetry, busy }) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        borderRadius: 8,
        background: "color-mix(in oklab, var(--amber) 12%, var(--card))",
        border: "1px solid color-mix(in oklab, var(--amber) 40%, var(--border))",
        minWidth: 0,
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          color: "color-mix(in oklab, var(--amber) 85%, var(--ink))",
          display: "inline-flex",
          flexShrink: 0,
        }}
      >
        <WarnGlyph />
      </span>
      <div style={{ flex: "1 1 220px", minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{title}</div>
        {hint && <div style={{ fontSize: 12, color: "var(--muted)" }}>{hint}</div>}
      </div>
      {onRetry && (
        <button
          type="button"
          className="btn"
          onClick={onRetry}
          disabled={busy}
          style={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {translate("myMachinery:v2.tabs.retry")}
          <span aria-hidden style={{ marginLeft: 6 }}>&#8635;</span>
        </button>
      )}
    </div>
  );
};

export default RetryNotice;
